import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { adminApi, type NewsArticle } from '@/services/api';

type EditForm = Partial<NewsArticle>;

export default function AdminNewsPage() {
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [showEditor, setShowEditor] = useState(false);
  const [editItem, setEditItem] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminApi
      .listNews({ page: 1, pageSize: 100 })
      .then((res) => setNews(res.list))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = filter ? news.filter((n) => n.status === filter) : news;

  const toggleStatus = (item: NewsArticle) => {
    const next = item.status === 'published' ? 'offline' : 'published';
    adminApi.setNewsStatus(item.id, next).then(fetchData).catch(() => {});
  };

  const handleSave = async () => {
    if (!editItem || !editItem.title) return;
    setSaving(true);
    try {
      if (editItem.id) {
        await adminApi.updateNews(editItem.id, editItem);
      } else {
        await adminApi.createNews(editItem);
      }
      setShowEditor(false);
      setEditItem(null);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground-950">资讯管理</h2>
            <p className="text-sm text-foreground-500 mt-0.5">共 {news.length} 篇文章</p>
          </div>
          <button
            onClick={() => { setEditItem({ title: '', category: '', author: '', content: '' }); setShowEditor(true); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-foreground-950 text-white hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            新建文章
          </button>
        </div>

        <div className="flex items-center gap-2">
          {['', 'published', 'draft', 'offline'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filter === s ? 'bg-foreground-950 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {s === '' ? '全部' : s === 'published' ? '已发布' : s === 'draft' ? '草稿' : '已下架'}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">标题</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden sm:table-cell">分类</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden md:table-cell">作者</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden lg:table-cell">阅读量</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">状态</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-10 text-foreground-400">加载中...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-foreground-400">暂无文章</td></tr>
                ) : (
                  filtered.map((item) => (
                    <tr key={item.id} className="border-b border-background-100 hover:bg-background-50/50">
                      <td className="px-4 py-3 text-foreground-900 font-medium max-w-[200px] truncate">{item.title}</td>
                      <td className="px-4 py-3 text-foreground-600 hidden sm:table-cell whitespace-nowrap">{item.category}</td>
                      <td className="px-4 py-3 text-foreground-500 hidden md:table-cell">{item.author}</td>
                      <td className="px-4 py-3 text-foreground-700 hidden lg:table-cell">{item.readCount.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'published' ? 'bg-green-100 text-green-700' :
                          item.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                          'bg-background-200 text-foreground-500'
                        }`}>
                          {item.status === 'published' ? '已发布' : item.status === 'draft' ? '草稿' : '已下架'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setEditItem(item); setShowEditor(true); }}
                            className="px-2.5 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => toggleStatus(item)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              item.status === 'published'
                                ? 'text-foreground-500 hover:bg-background-100'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                          >
                            {item.status === 'published' ? '下架' : '发布'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Editor modal */}
        {showEditor && editItem && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/40" onClick={() => { setShowEditor(false); setEditItem(null); }} />
            <div className="relative bg-white rounded-xl p-5 w-full max-w-lg border border-background-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">{editItem.id ? '编辑文章' : '新建文章'}</h3>
                <button onClick={() => { setShowEditor(false); setEditItem(null); }} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">标题</label>
                  <input
                    type="text"
                    value={editItem.title || ''}
                    onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm focus:outline-none focus:border-foreground-400"
                    placeholder="输入文章标题"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-foreground-600 mb-1">分类</label>
                    <select
                      value={editItem.category || ''}
                      onChange={(e) => setEditItem({ ...editItem, category: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm focus:outline-none focus:border-foreground-400 cursor-pointer bg-white"
                    >
                      <option value="">选择分类</option>
                      <option value="市场动态">市场动态</option>
                      <option value="政策法规">政策法规</option>
                      <option value="行业研究">行业研究</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-foreground-600 mb-1">作者</label>
                    <input
                      type="text"
                      value={editItem.author || ''}
                      onChange={(e) => setEditItem({ ...editItem, author: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm focus:outline-none focus:border-foreground-400"
                      placeholder="作者署名"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">摘要</label>
                  <input
                    type="text"
                    value={editItem.summary || ''}
                    onChange={(e) => setEditItem({ ...editItem, summary: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm focus:outline-none focus:border-foreground-400"
                    placeholder="文章摘要"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">正文内容</label>
                  <textarea
                    value={editItem.content || ''}
                    onChange={(e) => setEditItem({ ...editItem, content: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm focus:outline-none focus:border-foreground-400 h-40 resize-none"
                    placeholder="输入文章正文..."
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={handleSave} disabled={saving || !editItem.title} className="flex-1 py-2 rounded-lg text-sm font-medium bg-foreground-950 text-white hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50">
                    {saving ? '保存中...' : '保存'}
                  </button>
                  <button onClick={() => { setShowEditor(false); setEditItem(null); }} className="px-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">取消</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
